import { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import { useAuth } from '@/auth';
import { useTenant } from '@/auth/useTenant';
import { DEPLOY_ENVIRONMENT } from './posthog';

/**
 * PostHogUserSync
 *
 * Synchronizes user identity and tenant context with PostHog.
 * Must be rendered inside both AuthProvider and PostHogProvider.
 *
 * - When user logs in: identifies the user, registers tenant as a super property and joins the tenant group
 * - When user logs out: resets PostHog identity
 *
 * Uses usePostHog() hook which only returns the instance when PostHog is loaded,
 * avoiding race conditions with initialization.
 */
export function PostHogUserSync(): null {
  const { user, isAuthenticated } = useAuth();
  const posthog = usePostHog();
  const { domain, name: tenantName } = useTenant();

  useEffect(() => {
    if (!posthog) return;

    try {
      if (isAuthenticated && user) {
        const role = user.tenant_roles?.[domain]?.role;
        posthog.identify(String(user.id), {
          email: user.email,
          username: user.username,
          display: user.display,
        });
        posthog.register({ tenant: domain, tenant_name: tenantName || domain, role: role ?? 'unknown', environment: DEPLOY_ENVIRONMENT });
        posthog.group('tenant', domain, { name: tenantName || domain });
      } else {
        posthog.reset();
      }
    } catch (error) {
      console.error('Failed to sync user identity with PostHog:', error);
    }
  }, [posthog, isAuthenticated, user, domain, tenantName]);

  return null;
}
